import React from "react";
import { Modal, Input, Spin, Alert, Divider, Typography } from "antd";
import useDecisionForm from "../hooks/useDecisionForm";
import ApproverPaymentSplits from "./ApproverPaymentSplits";

const { Text } = Typography;

/**
 * The shared approve/reject modal.
 *
 * Some decisions need input that only exists at the moment of deciding. A bank
 * payout is the case in hand: its UTR is created when the approver actually makes
 * the transfer, so it cannot have been recorded by the maker. When `instanceId` is
 * given, the modal asks the server what this particular decision needs
 * (GET /approval/decision-form) and renders it above the comment box; for every
 * entity type that needs nothing, the modal is exactly what it always was.
 *
 * `onOk` is called with that extra input as its first argument — callers that
 * ignore it keep working unchanged.
 */
const ApprovalActionModal = ({
  visible,
  actionType,
  comment,
  onChange,
  onOk,
  onCancel,
  loading,
  instanceId,
}) => {
  const isApprove = actionType === "approved";
  const {
    form, splits, setSplits, loading: loadingForm, error: loadError, errors: splitErrors, blocked, payload,
  } = useDecisionForm(instanceId, { enabled: visible && isApprove });

  const handleOk = () => {
    if (blocked) return;
    onOk?.(payload);
  };

  return (
    <Modal
      open={visible}
      title={isApprove ? "Approve" : "Reject"}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={isApprove ? "Approve" : "Reject"}
      okButtonProps={{
        type: isApprove ? "primary" : "danger",
        disabled: blocked || loadingForm,
      }}
      width={form ? 960 : 520}
      destroyOnClose
    >
      {loadingForm && (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <Spin size="small" />{" "}
          <Text type="secondary" style={{ marginLeft: 8 }}>Loading payment details…</Text>
        </div>
      )}

      {loadError && (
        <Alert type="warning" showIcon message={loadError} style={{ marginBottom: 12 }} />
      )}

      {form && (
        <>
          <ApproverPaymentSplits data={form} value={splits} onChange={setSplits} />
          {splitErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Finish the payment breakdown before approving"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {splitErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              }
            />
          )}
          <Divider style={{ margin: "12px 0" }} />
        </>
      )}

      <Input.TextArea
        rows={4}
        placeholder="Add a comment (required)"
        value={comment}
        onChange={onChange}
      />
    </Modal>
  );
};

export default ApprovalActionModal;
